import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { RadialBarChart, RadialBar, Legend, ResponsiveContainer, Tooltip, PolarAngleAxis } from 'recharts';
import roiBg from '../assets/roi-calc.jpg';

const ROICalculator = () => {
  const [headCount, setHeadCount] = useState(0);
  const [hoursPerDay, setHoursPerDay] = useState(0);
  const [hourlyRate, setHourlyRate] = useState(0);
  const [selectedProcess, setSelectedProcess] = useState('');

  const manualAnnualCost = headCount * hoursPerDay * hourlyRate * 260;
  const aiAnnualCost = manualAnnualCost * 0.35;
  const costSaving = manualAnnualCost - aiAnnualCost;

  const manualManHours = headCount * hoursPerDay * 260;
  const aiManHours = manualManHours * 0.35;
  const effortSaving = manualManHours - aiManHours;

  const costData = [
    { name: 'Manual', value: Math.min(manualAnnualCost, 4000000), fill: 'url(#gradientManual)' },
    { name: 'AI', value: Math.min(aiAnnualCost, 4000000), fill: 'url(#gradientAI)' },
  ];

  const effortData = [
    { name: 'Manual', value: Math.min(manualManHours, 50000), fill: 'url(#gradientManual)' },
    { name: 'AI', value: Math.min(aiManHours, 50000), fill: 'url(#gradientAI)' },
  ];

  const costTicks = [0, 666666, 1333333, 2000000, 2666666, 3333333, 4000000];
  const effortTicks = [0, 8333, 16666, 25000, 33333, 41666, 50000];

  const formatCostTick = (tick) => {
    const map = {
      0: '0',
      666666: '720k',
      1333333: '1000k',
      2000000: '2160k',
      2666666: '2880k',
      3333333: '3600k',
      4000000: '4000k',
    };
    return map[tick] || '';
  };

  const formatEffortTick = (tick) => {
    const map = {
      0: '0',
      8333: '9.6k',
      16666: '10k',
      25000: '28.8k',
      33333: '38.4k',
      41666: '48k',
      50000: '50k',
    };
    return map[tick] || '';
  };

  // ✅ WebApplication Schema for ROI Calculator
  const webAppSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Healthcare RCM ROI Calculator",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Web Browser",
    "description": "Free calculator to estimate cost savings from healthcare revenue cycle management outsourcing and medical billing automation",
    "url": "https://mdercm.com/#roi-calculator",
    "browserRequirements": "Requires JavaScript. Works on all modern browsers.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "156",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "Calculate annual cost savings",
      "Estimate effort reduction",
      "Compare manual vs AI-powered RCM",
      "Instant ROI analysis"
    ],
    "screenshot": "https://mdercm.com/roi-calculator-screenshot.jpg",
    "author": {
      "@type": "Organization",
      "name": "MDeRCM Solutions LLC",
      "url": "https://mdercm.com"
    }
  };

  // ✅ HowTo Schema for Healthcare Cost Reduction
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Calculate Healthcare Revenue Cycle Management Savings",
    "description": "Step-by-step guide to calculate your potential savings with outsourced revenue cycle management services",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Select Process",
        "text": "Choose the RCM process you want to automate (billing, claims, denials, etc.)"
      },
      {
        "@type": "HowToStep",
        "name": "Enter Staff Count",
        "text": "Input the number of staff currently handling this process"
      },
      {
        "@type": "HowToStep",
        "name": "Add Hours Per Day",
        "text": "Specify how many hours per day each staff member spends on this task"
      },
      {
        "@type": "HowToStep",
        "name": "Input Hourly Rate",
        "text": "Enter the average hourly rate per employee"
      },
      {
        "@type": "HowToStep",
        "name": "View Results",
        "text": "See your annual cost savings and effort reduction with AI-powered automation"
      }
    ],
    "totalTime": "PT2M"
  };

  return (
    <div
      style={{
        backgroundImage: `linear-gradient(rgba(15,32,39,0.85), rgba(15,32,39,0.85)), url(${roiBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        color: 'white',
        fontFamily: 'Poppins, sans-serif',
        minHeight: '100vh',
        padding: '80px 5vw',
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      {/* ✅ Add Schema to Head */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(webAppSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>

      {/* Hidden image for SEO */}
      <img 
        src={roiBg} 
        alt="Healthcare cost reduction calculator - Calculate ROI from medical billing outsourcing services" 
        style={{ display: 'none' }}
      />

      {/* ✅ SEO-OPTIMIZED H2 with Keywords */}
      <h2
        style={{
          fontSize: 'clamp(2rem, 5vw, 3rem)',
          fontWeight: '700',
          textAlign: 'center',
          marginBottom: '20px',
          lineHeight: '1.2',
        }}
      >
        Healthcare Cost Reduction Calculator - Free ROI Estimator
      </h2>

      {/* Supporting paragraph with keywords and internal links */}
      <p style={{
        fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
        textAlign: 'center',
        maxWidth: '900px',
        margin: '0 auto 50px',
        lineHeight: '1.7',
        color: '#e0e0e0',
      }}
      >
        Calculate your savings with our <strong>outsourced revenue cycle management services</strong>. 
        See how AI-powered{' '}
        <Link 
          to="/healthcare/ai" 
          style={{ color: '#00ffff', textDecoration: 'underline' }}
          aria-label="Explore AI-powered healthcare solutions"
        >
          medical billing outsourcing
        </Link>{' '}
        can reduce your operational costs by up to 65%. Get instant results on <strong>how to improve revenue cycle management</strong> efficiency.
      </p>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: '40px',
          maxWidth: '1400px',
          margin: '0 auto',
        }}
      >
        {/* Left Input Panel */}
        <div style={{ 
          flex: '1 1 350px', 
          maxWidth: '400px', 
          minWidth: '300px',
          backgroundColor: 'rgba(255,255,255,0.05)',
          padding: '30px',
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
        }}
        role="form"
        aria-label="ROI Calculator Input Form"
        >
          <h3 style={{
            fontSize: '1.3rem',
            fontWeight: '600',
            marginBottom: '20px',
            color: '#00ffff',
          }}>
            Calculate Your RCM Savings
          </h3>

          <label 
            htmlFor="process-select"
            style={{ display: 'block', marginBottom: '12px', fontSize: '1rem', fontWeight: '500' }}
          >
            Select Process to Automate
          </label>
          <select
            id="process-select"
            value={selectedProcess}
            onChange={(e) => {
              setSelectedProcess(e.target.value);
              setHeadCount(0);
              setHoursPerDay(0);
              setHourlyRate(0);
            }}
            style={{ 
              width: '100%', 
              padding: '12px', 
              borderRadius: '8px', 
              marginBottom: '25px',
              fontSize: '0.95rem',
              border: 'none',
            }}
            aria-label="Select healthcare RCM process to automate"
          >
            <option value="">Select your process</option>
            <option>Appointment scheduling</option>
            <option>Insurance eligibility verification</option>
            <option>Pre-authorizations</option>
            <option>Physician notifications</option>
            <option>Appointment reminders</option>
            <option>Billing and EDI</option>
            <option>Denial management</option>
            <option>Claim submissions</option>
            <option>Claim status tracking</option>
            <option>Insurance follow ups</option>
            <option>Accounts receivable</option>
            <option>EOB posting</option>
            <option>Request for information</option>
            <option>Compliance and audits</option>
            <option>Reporting and metrics</option>
            <option>Custom workflow</option>
          </select>

          <label 
            htmlFor="staff-headcount"
            style={{ fontSize: '1rem', fontWeight: '500' }}
          >
            Staff Headcount
          </label>
          <input
            id="staff-headcount"
            type="range"
            min="0"
            max="100"
            value={headCount}
            onChange={(e) => setHeadCount(parseInt(e.target.value))}
            style={{ width: '100%', marginTop: '10px' }}
            aria-label="Number of staff members"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={headCount}
          />
          <div style={{ marginBottom: '25px', fontSize: '1.5rem', fontWeight: '700', color: '#00ffff' }}>
            {headCount} people
          </div>

          <label 
            htmlFor="hours-per-day"
            style={{ fontSize: '1rem', fontWeight: '500' }}
          >
            Hours per Person per Day
          </label>
          <input
            id="hours-per-day"
            type="range"
            min="0"
            max="8"
            value={hoursPerDay}
            onChange={(e) => setHoursPerDay(parseInt(e.target.value))}
            style={{ width: '100%', marginTop: '10px' }}
            aria-label="Hours spent per day on this process"
            aria-valuemin="0"
            aria-valuemax="8"
            aria-valuenow={hoursPerDay}
          />
          <div style={{ marginBottom: '25px', fontSize: '1.5rem', fontWeight: '700', color: '#00ffff' }}>
            {hoursPerDay} hours/day
          </div>

          <label 
            htmlFor="hourly-rate"
            style={{ fontSize: '1rem', fontWeight: '500' }}
          >
            Hourly Rate per Person
          </label>
          <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
            <span style={{ marginRight: '8px', fontSize: '1.3rem' }}>$</span>
            <input
              id="hourly-rate"
              type="number"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
              style={{ 
                width: '120px', 
                padding: '10px', 
                borderRadius: '6px',
                fontSize: '1rem',
                border: 'none',
              }}
              placeholder="0"
              aria-label="Average hourly rate in dollars"
            />
          </div>

          {/* Internal Link to Services */}
          <div style={{
            marginTop: '30px',
            padding: '20px',
            backgroundColor: 'rgba(0, 191, 255, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(0, 191, 255, 0.3)',
          }}>
            <p style={{ fontSize: '0.9rem', marginBottom: '15px', lineHeight: '1.6' }}>
              💡 Want to learn more about specific RCM processes?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Link 
                to="/healthcare/ai/denial-management"
                style={{ color: '#00ffff', fontSize: '0.85rem', textDecoration: 'underline' }}
              >
                → AI Denial Management
              </Link>
              <Link 
                to="/healthcare/ai/eligibility-check"
                style={{ color: '#00ffff', fontSize: '0.85rem', textDecoration: 'underline' }}
              >
                → Eligibility Verification
              </Link>
              <Link 
                to="/healthcare/ai/payment-posting"
                style={{ color: '#00ffff', fontSize: '0.85rem', textDecoration: 'underline' }}
              >
                → Payment Posting
              </Link>
            </div>
          </div>
        </div>

        {/* Cost Comparison */}
        <div style={{ 
          flex: '1 1 350px', 
          maxWidth: '450px', 
          minWidth: '300px', 
          textAlign: 'center', 
          position: 'relative',
          backgroundColor: 'rgba(255,255,255,0.05)',
          padding: '30px 20px',
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
        }}
        role="region"
        aria-label="Annual cost comparison results"
        >
          <div
            style={{
              position: 'absolute',
              top: '160px',
              left: '40%',
              transform: 'translateX(-50%)',
              fontWeight: 'bold',
              fontSize: '1.3rem',
              lineHeight: '1.6',
              textAlign: 'left',
              zIndex: 10,
            }}
            aria-live="polite"
          >
            <div style={{ color: '#00bfff' }}>Manual: ${manualAnnualCost.toLocaleString()}</div>
            <div style={{ color: '#ff4f81' }}>With AI: ${aiAnnualCost.toLocaleString()}</div>
          </div>

          <div style={{ textAlign: 'left', display: 'inline-block', marginBottom: '20px' }}>
            <h3
              style={{
                borderBottom: '3px solid #00bfff',
                display: 'inline-block',
                paddingBottom: '6px',
                marginBottom: '12px',
                fontSize: '1.3rem',
              }}
            >
              Annual Cost Comparison
            </h3>
            <p style={{ fontSize: '1.8rem', fontWeight: '700', margin: 0, color: '#00ff88' }}>
              Save: ${costSaving.toLocaleString()}
            </p>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <RadialBarChart innerRadius="30%" outerRadius="90%" data={costData} startAngle={90} endAngle={-180}>
              <defs>
                <linearGradient id="gradientManual" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#00bfff" />
                  <stop offset="100%" stopColor="#0077be" />
                </linearGradient>
                <linearGradient id="gradientAI" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#ff4f81" />
                  <stop offset="100%" stopColor="#b0003a" />
                </linearGradient>
              </defs>
              <PolarAngleAxis
                type="number"
                domain={[0, 4000000]}
                ticks={costTicks}
                tickFormatter={formatCostTick}
                tick={{ fill: 'white', fontSize: 12 }}
                tickLine={false}
                tickSize={-15}
              />
              <RadialBar minAngle={15} background clockWise dataKey="value" cornerRadius={10} />
              <Legend iconSize={10} layout="vertical" verticalAlign="top" align="right" wrapperStyle={{ right: 0, top: 40 }} />
              <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
            </RadialBarChart>
          </ResponsiveContainer>
        </div>

        {/* Effort Comparison */}
        <div style={{ 
          flex: '1 1 350px', 
          maxWidth: '450px', 
          minWidth: '300px', 
          textAlign: 'center', 
          position: 'relative',
          backgroundColor: 'rgba(255,255,255,0.05)',
          padding: '30px 20px',
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
        }}
        role="region"
        aria-label="Annual effort comparison results"
        >
          <div
            style={{
              position: 'absolute',
              top: '160px',
              left: '40%',
              transform: 'translateX(-50%)',
              fontWeight: 'bold',
              fontSize: '1.3rem',
              lineHeight: '1.6',
              textAlign: 'left',
              zIndex: 10,
            }}
            aria-live="polite"
          >
            <div style={{ color: '#00bfff' }}>Manual: {manualManHours.toLocaleString()}h</div>
            <div style={{ color: '#ff4f81' }}>With AI: {aiManHours.toLocaleString()}h</div>
          </div>

          <div style={{ textAlign: 'left', display: 'inline-block', marginBottom: '20px' }}>
            <h3
              style={{
                borderBottom: '3px solid #00bfff',
                display: 'inline-block',
                paddingBottom: '6px',
                marginBottom: '12px',
                fontSize: '1.3rem',
              }}
            >
              Annual Effort Comparison
            </h3>
            <p style={{ fontSize: '1.8rem', fontWeight: '700', margin: 0, color: '#00ff88' }}>
              Save: {effortSaving.toLocaleString()} hours
            </p>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <RadialBarChart innerRadius="30%" outerRadius="90%" data={effortData} startAngle={90} endAngle={-180}>
              <PolarAngleAxis
                type="number"
                domain={[0, 50000]}
                ticks={effortTicks}
                tickFormatter={formatEffortTick}
                tick={{ fill: 'white', fontSize: 12 }}
                tickLine={false}
                tickSize={-15}
              />
              <RadialBar minAngle={15} background clockWise dataKey="value" cornerRadius={10} />
              <Legend iconSize={10} layout="vertical" verticalAlign="top" align="right" wrapperStyle={{ right: 0, top: 40 }} />
              <Tooltip formatter={(value) => `${value.toLocaleString()} hrs`} />
            </RadialBarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom CTA with keywords and internal links */}
      <div style={{
        textAlign: 'center',
        marginTop: '60px',
        padding: '30px',
        backgroundColor: 'rgba(0, 119, 182, 0.15)',
        borderRadius: '12px',
        maxWidth: '900px',
        margin: '60px auto 0',
        backdropFilter: 'blur(10px)',
      }}>
        <p style={{
          fontSize: '1.2rem',
          lineHeight: '1.7',
          color: '#e0e0e0',
          marginBottom: '20px',
        }}>
          These savings are typical for healthcare providers using our{' '}
          <Link 
            to="/why" 
            style={{ color: '#00ffff', textDecoration: 'underline', fontWeight: '600' }}
            aria-label="Learn why choose MDeRCM for RCM consulting"
          >
            revenue cycle management consulting services
          </Link>. 
          Start your journey toward <strong style={{ color: '#00ffff' }}>healthcare cost reduction</strong> today.
        </p>

        {/* CTA Button */}
        <Link 
          to="/free-trial"
          style={{
            display: 'inline-block',
            padding: '15px 35px',
            backgroundColor: '#F26522',
            color: '#ffffff',
            borderRadius: '30px',
            fontSize: '1.05rem',
            fontWeight: '700',
            textDecoration: 'none',
            boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)',
            transition: 'all 0.3s ease',
            marginTop: '15px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#d3541c';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#F26522';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Get Free Consultation - Start Saving Today
        </Link>
      </div>

      {/* Mobile Responsive Styles */}
      <style>
        {`
          @media (max-width: 768px) {
            div[style*="flex: '1 1 350px'"] {
              flex: 1 1 100% !important;
              max-width: 100% !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ROICalculator;